import { jsx as _jsx } from "react/jsx-runtime";
import { addErrorNotification, createStandardOrder, getItemProfileForSite, getOrderSiteId, hasOrderBeenCreated, loadItemProfileForSite, logger, NotificationType, removeNotification, saveOrderItems } from '@vista/omnichannel-components-domain';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useI18n } from '../../context/occ/hooks/use-i18n';
import { useOccConfig } from '../../context/occ/hooks/use-occ-config';
import { ItemPicker } from '../../subcomponents/item-picker/item-picker';
import { ItemPickerMessage, ItemPickerMessageType } from '../../subcomponents/item-picker/subcomponents/message';
import { ItemPickerSkeleton } from '../../subcomponents/item-picker/subcomponents/skeleton';
import { validateItemSelection } from '../../subcomponents/item-picker/utils/validate-item-selection';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { areStringsTheSameOrUndefined } from '../../utils/string-utils';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { defaultSiteItemPickerProps, siteItemPickerPropsValidator } from './types/props';
const apiErrorNotificationId = getIdForComponentNotification('site-item-picker', NotificationType.Error, 'failed-to-save-item-selection');
export const SiteItemPickerBase = (props, ref) => {
    const i18n = useI18n();
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { displayText, assets, config, events, cdnOptions } = props;
    const [failedToLoadData, setFailedToLoadData] = useState(false);
    const orderSiteId = getOrderSiteId();
    if (!areStringsTheSameOrUndefined(props.siteId, orderSiteId)) {
        throw new Error("Cannot render the Site Item Picker component. The provided siteId doesn't match the siteId of the order.");
    }
    const siteId = props.siteId || orderSiteId;
    if (!siteId)
        throw new Error('Cannot render the Site Item Picker component without a siteId.');
    useEffect(() => {
        (async () => {
            try {
                await loadItemProfileForSite(siteId);
            }
            catch (error) {
                setFailedToLoadData(true);
                logger.error(`Failed to load items for site ${siteId}. ${error}`);
            }
        })();
    }, [siteId]);
    useImperativeHandle(ref, () => ({
        handleProceed
    }));
    const handleProceed = useCallback(async () => {
        removeNotification(apiErrorNotificationId);
        const result = validateItemSelection(config.minimumRequiredItems, displayText.insufficientItemsSelectedMessage, i18n);
        if (!result)
            return false;
        try {
            if (!hasOrderBeenCreated())
                await createStandardOrder(siteId);
            await saveOrderItems();
            return true;
        }
        catch (error) {
            addErrorNotification({
                id: apiErrorNotificationId,
                messages: [displayText.failedToSaveItemSelectionsMessage]
            }, error);
            return false;
        }
    }, [displayText, config, siteId, i18n]);
    if (failedToLoadData) {
        return (_jsx(ItemPickerMessage, { displayText: displayText, assets: assets, messageType: ItemPickerMessageType.FailedToLoadData }));
    }
    const itemProfile = getItemProfileForSite(siteId);
    if (!itemProfile)
        return enableSkeletonLoadingIndicators ? _jsx(ItemPickerSkeleton, { mode: config.mode }) : null;
    return (_jsx(ItemPicker, { itemProfilePages: itemProfile.pages, displayText: displayText, assets: assets, config: config, events: events, cdnOptions: cdnOptions }));
};
export const SiteItemPicker = observer(forwardRef(SiteItemPickerBase));
SiteItemPicker.displayName = 'SiteItemPicker';
/**
 * The **Site Item Picker** component renders the items available for a particular site, or for the site of the current
 * standard order.
 */
export const SiteItemPickerComponent = withComponentWrapper(SiteItemPicker, defaultSiteItemPickerProps, siteItemPickerPropsValidator);
